import { Card } from "@/components/ui/card";
import { PILLAR, BAND_COLORS, assignBand } from "@/lib/esg";

// Sector positioning widget — for each Q indicator shows a horizontal benchmark
// scale (P10 best → P90 worst) with the company's value plotted, color zones,
// and verdict chip.
export function SectorPositioning({ topics, clusterLabel, clusterCode }) {
  const items = [];
  for (const t of topics || []) {
    for (const ind of t.indicators || []) {
      if (ind.type === "Q" && ind.numeric_value !== null && ind.numeric_value !== undefined) {
        items.push({ ...ind, topic_code: t.topic_code, topic_label: t.label, pillar: t.pillar });
      }
    }
  }
  if (items.length === 0) {
    return (
      <Card data-testid="sector-positioning-card" className="lg:col-span-12 p-6 rounded-2xl shadow-soft border-slate-100">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Posizionamento Settoriale</h3>
        <p className="text-sm text-slate-500">Nessun indicatore quantitativo compilato. Inserisci valori per visualizzare il confronto con i benchmark del cluster.</p>
      </Card>
    );
  }
  const grouped = { E: [], S: [], G: [] };
  for (const it of items) grouped[it.pillar].push(it);

  return (
    <Card data-testid="sector-positioning-card" className="lg:col-span-12 rounded-2xl shadow-soft border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Posizionamento Settoriale</h3>
          <p className="text-sm text-slate-500">
            Confronto degli indicatori quantitativi con i benchmark del cluster <span className="font-semibold text-slate-700">{clusterCode}</span>
            {clusterLabel ? ` — ${clusterLabel}` : ""}. P10 = best practice, P90 = worst practice.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-viva-500" /> Eccellenza</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" /> Media settore</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500" /> Critico</span>
        </div>
      </div>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        {["E", "S", "G"].map((p) => grouped[p].length === 0 ? null : (
          <div key={p} className="lg:col-span-2">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${PILLAR[p].bg} ${PILLAR[p].text} text-xs font-semibold uppercase tracking-wider mb-4`}>
              {PILLAR[p].label} · {grouped[p].length} indicator{grouped[p].length > 1 ? "i" : "e"}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              {grouped[p].map((ind) => <BenchmarkBar key={ind.code} ind={ind} />)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BenchmarkBar({ ind }) {
  const v = ind.numeric_value;
  const p10 = ind.p10;
  const p90 = ind.p90;
  const dir = ind.direction;
  const unit = ind.unit || "";
  let pct;
  if (p90 === p10) pct = 50;
  else pct = ((v - p10) / (p90 - p10)) * 100;
  const visualPct = Math.max(0, Math.min(100, pct));
  const outOfRange = pct < -0.5 || pct > 100.5;

  const score = ind.score;
  const band = assignBand(score);
  const bandColor = band ? BAND_COLORS[band] : "#94A3B8";

  let verdict;
  if (score === null || score === undefined) verdict = "—";
  else if (score >= 80) verdict = "Eccellenza";
  else if (score >= 60) verdict = "Solido";
  else if (score >= 40) verdict = "Media settore";
  else if (score >= 20) verdict = "Sotto media";
  else verdict = "Critico";

  const gradient = dir === "neg"
    ? "linear-gradient(to right, #65972A55, #F59E0B33 50%, #EF444455)"
    : "linear-gradient(to right, #EF444455, #F59E0B33 50%, #65972A55)";

  const fmtNum = (n) => {
    if (n === null || n === undefined) return "—";
    if (Math.abs(n) >= 1000) return n.toLocaleString("it-IT");
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2).replace(/\.?0+$/, "");
  };

  return (
    <div className="space-y-2" data-testid={`bench-bar-${ind.code}`}>
      <div className="flex items-baseline gap-2 justify-between">
        <div className="min-w-0">
          <span className="text-xs font-bold text-slate-400 mr-2">{ind.code}</span>
          <span className="text-sm font-medium text-slate-900">{ind.question}</span>
        </div>
        <span
          className="shrink-0 px-2.5 py-1 rounded-full text-white text-xs font-heading font-bold"
          style={{ backgroundColor: bandColor }}
          title={verdict}
        >
          {band || "—"}
        </span>
      </div>

      <div className="relative pt-7 pb-7" data-testid={`bench-bar-track-${ind.code}`}>
        <div className="relative h-3 rounded-full border border-slate-200" style={{ background: gradient }}>
          <div className="absolute top-0 bottom-0 w-px bg-slate-300" style={{ left: "50%" }} />
          <div
            className="absolute -top-1.5 -translate-x-1/2 w-6 h-6 rounded-full border-2 border-white shadow-md"
            style={{ left: `${visualPct}%`, backgroundColor: bandColor }}
            data-testid={`bench-marker-${ind.code}`}
          />
          <div
            className="absolute -translate-x-1/2 -top-7 whitespace-nowrap"
            style={{ left: `${visualPct}%` }}
          >
            <span className="text-xs font-heading font-bold text-slate-900 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-soft">
              {fmtNum(v)}{unit && ` ${unit}`}
            </span>
          </div>
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-slate-500 font-medium">
          <span>
            <span className="font-bold text-slate-700">P10</span> · {fmtNum(p10)}
            <span className="ml-1 text-viva-600">{dir === "neg" ? "best" : "worst"}</span>
          </span>
          <span className="text-slate-400">P50</span>
          <span>
            <span className="font-bold text-slate-700">P90</span> · {fmtNum(p90)}
            <span className="ml-1 text-red-600">{dir === "neg" ? "worst" : "best"}</span>
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">{ind.topic_code} · {ind.topic_label}</span>
        <span className="text-slate-700 font-medium">
          {outOfRange && (pct < 0
            ? <span className="text-viva-600 mr-2">⇣ oltre P10</span>
            : <span className="text-red-600 mr-2">⇡ oltre P90</span>)}
          Score: <span className="font-heading font-bold text-slate-900">{score === null || score === undefined ? "—" : Math.round(score)}</span> · {verdict}
        </span>
      </div>
    </div>
  );
}
